import { supabase, DEFAULT_ORG_ID, signImagesDeep, getActiveOrgOverride } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { getCurrentPlan } from './billing'
import { parseLocalDate } from '../dateUtils'

const PLAN_FREE_CODE = 'starter'
const TOPE_JUGADORES_GRUPO_FREE = 15

// =====================================================
// Inscripciones (torneos_inscripciones)
// =====================================================

export const getInscripcionesByTorneo = async (torneoId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .select(`
        *,
        grupos:grupo_id (
          id, nombre_del_grupo, categoria_base, rama, deporte, tipo_de_grupo
        ),
        categorias:categoria_id (
          id, nombre, edad_min, edad_max
        )
      `)
      .eq('torneo_id', torneoId)
      .eq('activo', true)
      .order('created_at', { ascending: true })

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getInscripcionesByTorneo]', error)
    return { data: [], error }
  }
}

export const getInscripcionById = async (inscripcionId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .select(`
        *,
        grupos:grupo_id (
          id, nombre_del_grupo, categoria_base, rama, deporte, tipo_de_grupo
        ),
        torneos:torneo_id (
          id, nombre, fecha_inicio, fecha_fin, formato, org_organizadora_id, estatus,
          fecha_limite_roster, max_jugadores_grupo, max_staff_grupo, dorsal_unico, playoff_requiere_participacion,
          ligas:liga_id (id, nombre, logo_url)
        ),
        categorias:categoria_id (id, nombre)
      `)
      .eq('id', inscripcionId)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[getInscripcionById]', error)
    return { data: null, error }
  }
}

export const getInscripcionesByGrupoIds = async (grupoIds = []) => {
  if (!Array.isArray(grupoIds) || grupoIds.length === 0) {
    return { data: [], error: null }
  }
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .select(`
        *,
        grupos:grupo_id (id, nombre_del_grupo, rama, deporte),
        torneos:torneo_id!inner (
          id, nombre, fecha_inicio, fecha_fin, estatus, etapa, tipo_torneo, formato, activo,
          org_organizadora_id, temporada_id,
          ligas:liga_id (nombre, logo_url),
          temporadas:temporada_id (id, nombre, fecha_inicio, fecha_fin, estado)
        )
      `)
      .in('grupo_id', grupoIds)
      .eq('activo', true)
      .eq('torneos.activo', true)

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getInscripcionesByGrupoIds]', error)
    return { data: [], error }
  }
}

export const updateInscripcion = async (inscripcionId, patch) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', inscripcionId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateInscripcion]', error)
    return { data: null, error }
  }
}

export const deleteInscripcion = async (inscripcionId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .update({ activo: false, estatus: 'inactivo', deleted_at: new Date().toISOString() })
      .eq('id', inscripcionId)
      .select('id')
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[deleteInscripcion]', error)
    return { data: null, error }
  }
}

// =====================================================
// Inscripcion del Club a un torneo (RPCs cross-org, #3)
// =====================================================

export const tieneLicenciaTorneo = async (torneoId) => {
  try {
    const { data, error } = await supabase.rpc('tiene_licencia_torneo', {
      p_torneo_id: torneoId
    })
    if (error) throw error
    return { data: Boolean(data), error: null }
  } catch (error) {
    console.error('[tieneLicenciaTorneo]', error)
    return { data: false, error }
  }
}

export const getTorneoCategoriasConvocadas = async (torneoId) => {
  try {
    const { data, error } = await supabase.rpc('get_torneo_categorias_convocadas', {
      p_torneo_id: torneoId
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getTorneoCategoriasConvocadas]', error)
    return { data: [], error }
  }
}

export const crearGrupoTorneo = async ({ torneoId, categoriaId = null, convocatoriaId = null, nombre }) => {
  try {
    // La division (categoria + genero, 20261005) decide el genero del grupo nuevo.
    // p_convocatoria_id solo viaja si hay division: contra una base sin la migracion
    // 20261005 las llamadas que no la usan siguen funcionando.
    const params = {
      p_torneo_id: torneoId,
      p_categoria_id: categoriaId || null,
      p_nombre: nombre
    }
    if (convocatoriaId) params.p_convocatoria_id = convocatoriaId
    const { data, error } = await supabase.rpc('crear_grupo_torneo', params)
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[crearGrupoTorneo]', error)
    return { data: null, error }
  }
}

// El torneo lo organiza mi propio club: es un torneo LOCAL para mi.
// Es la pregunta que decide todo el flujo de inscripcion.
export const esTorneoPropio = async (torneoId) => {
  try {
    const { data, error } = await supabase.rpc('es_torneo_propio', {
      p_torneo_id: torneoId
    })
    if (error) throw error
    return { data: Boolean(data), error: null }
  } catch (error) {
    console.error('[esTorneoPropio]', error)
    return { data: false, error }
  }
}

// Las categorias del grupo. En un torneo local es de aqui de donde sale la categoria
// de la inscripcion: el contenedor que manda es el grupo.
export const getCategoriasDeGrupo = async (grupoId) => {
  try {
    const { data, error } = await supabase
      .from('grupos_categorias')
      .select('categoria_id, categorias:categoria_id (id, nombre, edad_min, edad_max, anio_inicio, anio_fin, deporte, rama)')
      .eq('grupo_id', grupoId)
    if (error) throw error
    return {
      data: (data || []).map((row) => row.categorias).filter(Boolean),
      error: null
    }
  } catch (error) {
    console.error('[getCategoriasDeGrupo]', error)
    return { data: [], error }
  }
}

// Inscribe un grupo. El RPC bifurca solo: si el torneo es del propio club queda
// inscrito (categoria heredada del grupo); si es de otra org, queda solicitado y la
// organizadora resuelve.
export const inscribirGrupo = async ({ torneoId, grupoId, categoriaId = null, convocatoriaId = null }) => {
  try {
    // En la red viaja la division (convocatoria_id, 20261005); en interno, solo la
    // categoria del grupo.
    // p_convocatoria_id solo viaja si hay division (ver crearGrupoTorneo).
    const params = {
      p_torneo_id: torneoId,
      p_grupo_id: grupoId,
      p_categoria_id: categoriaId || null
    }
    if (convocatoriaId) params.p_convocatoria_id = convocatoriaId
    const { data, error } = await supabase.rpc('inscribir_grupo', params)
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[inscribirGrupo]', error)
    return { data: null, error }
  }
}

// Los torneos a los que este club puede inscribir un grupo: los suyos (LOCALES) y
// aquellos donde le aceptaron la invitacion (EXTERNOS).
//
// Antes solo listaba los externos, asi que un club no encontraba por ningun lado el
// torneo que el mismo acababa de crear.
// Los torneos INTERNOS donde se puede inscribir un grupo desde su ficha: los que
// organiza el propio club y siguen abiertos. Solo internos, a proposito (2026-09-05):
// esta lista juntaba tambien los de la red (los de invitacion aceptada) y era la unica
// pantalla que mezclaba los dos mundos en una lista. En la red se inscribe desde
// MYKLUBB Torneos, que tiene su propio boton por torneo.
export const getTorneosParaInscribir = async (orgId = null) => {
  try {
    const miOrg = orgId || getActiveOrgOverride() || (await getCurrentStaff())?.data?.org_id || null
    if (!miOrg) return { data: [], error: null }

    const { data, error } = await supabase
      .from('torneos')
      .select('id, nombre, sede, fecha_inicio, fecha_fin, logo_url, estatus, org_organizadora_id, ligas:liga_id (nombre), temporadas:temporada_id (deporte_id)')
      .eq('org_organizadora_id', miOrg)
      .eq('activo', true)
      .not('estatus', 'in', '("finalizado","cancelado")')
      .order('fecha_inicio', { ascending: false, nullsFirst: false })

    if (error) throw error
    return { data: (data || []).map((t) => ({ ...t, esLocal: true })), error: null }
  } catch (error) {
    console.error('[getTorneosParaInscribir]', error)
    return { data: [], error }
  }
}

// OBSOLETA: el RPC solicitar_inscripcion ahora solo delega en inscribir_grupo.
// Se conserva mientras queden llamadas viejas.
export const solicitarInscripcion = async ({ torneoId, grupoId, categoriaId, convocatoriaId }) =>
  inscribirGrupo({ torneoId, grupoId, categoriaId, convocatoriaId })

// Cap efectivo del roster por grupo: min(15, regla del organizador) en plan Free,
// regla del organizador (o sin tope) en planes de pago.
// `interno`: el torneo lo organiza el propio club, asi que no hay regla de organizador
// (2026-09-06, "los torneos internos no tienen limites de roster"). El tope del plan
// Free si se queda: es del plan del club, no del torneo.
export const getMaxJugadoresGrupo = async (torneoId, clubOrgId, { interno = false } = {}) => {
  try {
    let reglaOrganizador = null
    if (!interno) {
      const { data: torneo, error: torneoErr } = await supabase
        .from('torneos')
        .select('max_jugadores_grupo')
        .eq('id', torneoId)
        .single()
      if (torneoErr) throw torneoErr
      reglaOrganizador = torneo?.max_jugadores_grupo ?? null
    }
    const planCode = await getCurrentPlan(clubOrgId)
    const esFree = planCode === PLAN_FREE_CODE

    if (esFree) {
      const data = reglaOrganizador === null
        ? TOPE_JUGADORES_GRUPO_FREE
        : Math.min(TOPE_JUGADORES_GRUPO_FREE, reglaOrganizador)
      return { data, error: null }
    }

    return { data: reglaOrganizador, error: null }
  } catch (error) {
    console.error('[getMaxJugadoresGrupo]', error)
    return { data: null, error }
  }
}

// Elegibilidad por edad/año de nacimiento (funcion pura, sin supabase).
// categoriaRules: {edad_min, edad_max, anio_inicio, anio_fin} (de get_torneo_categorias_convocadas).
// Convencion del repo (ver GroupRosterModal.isPlayerEligibleForGroup): anio_fin = año de
// nacimiento minimo permitido (jugador mas grande), anio_inicio = año de nacimiento maximo
// permitido (jugador mas chico). Si no vienen explicitos, se derivan de edad_min/edad_max.
export const jugadoresElegibles = (categoriaRules, jugadores = []) => {
  const rules = categoriaRules || {}
  const currentYear = new Date().getFullYear()
  const edadMin = typeof rules.edad_min === 'number' ? rules.edad_min : null
  const edadMax = typeof rules.edad_max === 'number' ? rules.edad_max : null
  const anioInicio = typeof rules.anio_inicio === 'number'
    ? rules.anio_inicio
    : (edadMin !== null ? currentYear - edadMin : null)
  const anioFin = typeof rules.anio_fin === 'number'
    ? rules.anio_fin
    : (edadMax !== null ? currentYear - edadMax : null)

  const minYear = anioFin
  const maxYear = anioInicio
  const sinLimite = minYear === null && maxYear === null

  return (jugadores || []).map((jugador) => {
    const fecha = jugador?.fecha_nacimiento
    const birthDate = fecha ? parseLocalDate(fecha) : null

    if (!birthDate || Number.isNaN(birthDate.getTime())) {
      return { ...jugador, elegible: true, motivo: 'sin fecha' }
    }

    if (sinLimite) {
      return { ...jugador, elegible: true }
    }

    const birthYear = birthDate.getFullYear()
    const fueraDeRango = (minYear !== null && birthYear < minYear)
      || (maxYear !== null && birthYear > maxYear)

    if (fueraDeRango) {
      return { ...jugador, elegible: false, motivo: `Fuera de rango de edad de la categoria (nacido en ${birthYear})` }
    }

    return { ...jugador, elegible: true }
  })
}

export const recalcInscripcionRecord = async (inscripcionId) => {
  try {
    const { error } = await supabase.rpc('recalc_inscripcion_record', {
      p_inscripcion_id: inscripcionId
    })
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[recalcInscripcionRecord]', error)
    return { error }
  }
}

// =====================================================
// Roster oficial (torneos_inscripciones_jugadores)
// =====================================================

export const getRosterByInscripcion = async (inscripcionId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones_jugadores')
      .select(`
        *,
        jugadores:jugador_id (
          id, nombre_completo, nickname, numero, imagen_path, imagen_url, estatus
        )
      `)
      .eq('inscripcion_id', inscripcionId)
      .neq('status', 'no_convocado')
      .order('created_at', { ascending: true })

    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getRosterByInscripcion]', error)
    return { data: [], error }
  }
}

export const getRosterEfectivo = async (inscripcionId) => {
  try {
    const { data, error } = await supabase.rpc('get_inscripcion_roster', {
      p_inscripcion_id: inscripcionId
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getRosterEfectivo]', error)
    return { data: [], error }
  }
}

export const precargarRosterDesdeGrupo = async (inscripcionId, grupoId, orgId) => {
  try {
    const { data: jugadoresGrupo, error: jgErr } = await supabase
      .from('jugadores_grupos')
      .select(`
        jugador_id,
        jugadores:jugador_id (id, numero, estatus, activo, deleted)
      `)
      .eq('grupo_id', grupoId)
      .eq('activo', true)
    if (jgErr) throw jgErr

    const filas = (jugadoresGrupo || [])
      .filter((row) => row.jugadores
        && row.jugadores.activo === true
        && row.jugadores.deleted !== true
        && row.jugadores.estatus !== 'Baja')
      .map((row) => ({
        inscripcion_id: inscripcionId,
        jugador_id: row.jugador_id,
        org_id: orgId || DEFAULT_ORG_ID,
        status: 'convocado',
        dorsal_torneo: row.jugadores?.numero ? parseInt(row.jugadores.numero, 10) || null : null
      }))

    if (filas.length === 0) return { data: [], error: null }

    const { data, error } = await supabase
      .from('torneos_inscripciones_jugadores')
      .upsert(filas, { onConflict: 'inscripcion_id,jugador_id', ignoreDuplicates: true })
      .select()

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[precargarRosterDesdeGrupo]', error)
    return { data: null, error }
  }
}

export const upsertRosterJugador = async (patch) => {
  try {
    const { inscripcionId, jugadorId, orgId } = patch
    const row = {
      inscripcion_id: inscripcionId,
      jugador_id: jugadorId,
      org_id: orgId || DEFAULT_ORG_ID,
      updated_at: new Date().toISOString()
    }
    if ('status' in patch) row.status = patch.status
    if ('dorsalTorneo' in patch) row.dorsal_torneo = patch.dorsalTorneo
    if ('posicion' in patch) row.posicion = patch.posicion
    if ('notas' in patch) row.notas = patch.notas

    const { data, error } = await supabase
      .from('torneos_inscripciones_jugadores')
      .upsert([row], { onConflict: 'inscripcion_id,jugador_id' })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[upsertRosterJugador]', error)
    return { data: null, error }
  }
}

export const removeRosterJugador = async (inscripcionId, jugadorId) => {
  try {
    // Un UPDATE que la RLS bloquea no lanza error: simplemente no encuentra filas
    // visibles para actualizar y devuelve 0 afectadas, callado. Sin el .select() de
    // abajo esto se leia como exito -- el checkbox se destildaba en pantalla y el
    // jugador volvia a aparecer marcado en el siguiente refresh.
    const { data, error } = await supabase
      .from('torneos_inscripciones_jugadores')
      .update({ status: 'no_convocado', updated_at: new Date().toISOString() })
      .eq('inscripcion_id', inscripcionId)
      .eq('jugador_id', jugadorId)
      .select('id')
    if (error) throw error
    if (!data || data.length === 0) {
      throw new Error('No se pudo actualizar el jugador.')
    }
    return { error: null }
  } catch (error) {
    console.error('[removeRosterJugador]', error)
    return { error }
  }
}

// =====================================================
// Staff autorizado (torneos_inscripciones_staff)
// =====================================================

export const getStaffByInscripcion = async (inscripcionId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones_staff')
      .select(`
        *,
        staff:staff_id (id, nombre_completo, email, telefono, rol, imagen_path, imagen_url)
      `)
      .eq('inscripcion_id', inscripcionId)
      .eq('activo', true)

    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getStaffByInscripcion]', error)
    return { data: [], error }
  }
}

export const upsertStaffInscripcion = async ({
  inscripcionId,
  staffId,
  orgId,
  rolTorneo = 'asistente',
  notas = null
}) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones_staff')
      .upsert(
        [{
          inscripcion_id: inscripcionId,
          staff_id: staffId,
          org_id: orgId || DEFAULT_ORG_ID,
          rol_torneo: rolTorneo,
          notas,
          activo: true,
          updated_at: new Date().toISOString()
        }],
        { onConflict: 'inscripcion_id,staff_id' }
      )
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[upsertStaffInscripcion]', error)
    return { data: null, error }
  }
}

export const removeStaffInscripcion = async (inscripcionId, staffId) => {
  try {
    // Mismo gemelo mudo que removeRosterJugador: un DELETE que la RLS bloquea no
    // lanza, borra 0 filas y calla. Sin el .select() de abajo esto se leia como
    // exito -- el checkbox se destildaba en pantalla y el staff seguia oficial.
    const { data, error } = await supabase
      .from('torneos_inscripciones_staff')
      .delete()
      .eq('inscripcion_id', inscripcionId)
      .eq('staff_id', staffId)
      .select('id')
    if (error) throw error
    if (!data || data.length === 0) {
      // Que paso, no que decirle al usuario: el texto de pantalla lo elige la pantalla
      // (motivoStaff en InscripcionDetail), que es la que sabe en que boton estamos.
      throw new Error('El DELETE no borro ninguna fila: la RLS lo bloqueo o ya no existia.')
    }
    return { error: null }
  } catch (error) {
    console.error('[removeStaffInscripcion]', error)
    return { error }
  }
}

// Llena el staff de la inscripcion con el del grupo, hasta el tope del torneo (RPC
// precargar_staff_inscripcion, 20260907). Solo red; en interno la base lo rechaza.
export const precargarStaffInscripcion = async (inscripcionId) => {
  try {
    const { data, error } = await supabase.rpc('precargar_staff_inscripcion', { p_inscripcion: inscripcionId })
    if (error) throw error
    return { data: data ?? 0, error: null }
  } catch (error) {
    console.error('[precargarStaffInscripcion]', error)
    return { data: 0, error }
  }
}

// El staff declarado, como lo ve la organizadora (nombre, rol, telefono, correo, foto).
// Es la misma RPC que usa la app de Torneos; el tarjeton de la red la usa aqui.
export const getStaffInscripcionRpc = async (inscripcionId) => {
  try {
    const { data, error } = await supabase.rpc('get_staff_inscripcion', { p_inscripcion_id: inscripcionId })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getStaffInscripcionRpc]', error)
    return { data: [], error }
  }
}
